import {redirect} from "react-router-dom";
import {router} from "../router";
import {getFrontendEnvironment} from "./environment";
import {clearSessionState, readSessionState, subscribeSessionState, writeSessionState} from "../hooks/sessionState";
import {Type} from "./typing";
import {Value} from "typebox/value";
import {AccountId} from "platformed-client/types";
import {isSafeInternalPath} from "./safeInternalPath";

export enum RedirectTarget {
  Unknown,
  Login,
  Register,
  AccountSelector,
  PortalSelector,
  ProductSelector,
}

// Subclass `Response` to add an easy to use "follow()" method
export class RedirectResponse extends Response {
  #target: RedirectTarget;

  constructor(url: string, target = RedirectTarget.Unknown) {
    const r = redirect(url);
    super(null, {status: r.status, headers: r.headers});
    this.#target = target;
  }
  get target(): RedirectTarget {
    return this.#target;
  }
  get location(): string {
    return this.headers.get("Location")!;
  }
  follow() {
    const loc = this.location;
    if (loc && isSafeInternalPath(loc)) {
      return router!.navigate(loc, {replace: true});
    }
    // Every RedirectResponse location built today is a same-origin "/" path, so
    // this branch is defensive: never hand an untrusted value to window.location
    // (a leading "//" is a protocol-relative URL -> external open redirect).
    window.location.replace("/");
  }
}

function canRedirectTo(path: string): boolean {
  const disallowedPrefixes = [
    "/login",
    "/register",
    "/logout",
    "/account-selector",
    "/portal-selector",
    "/request-reset",
    "/reset-login",
    "/sign-in",
    "/invite",
  ];
  return isSafeInternalPath(path) && disallowedPrefixes.every(prefix => !path.startsWith(prefix)) && path !== "/";
}

export function getRedirectParams(): URLSearchParams {
  const url = new URL(window.location.href);
  // Handle case where user is in the process of navigating to a new URL
  if (router!.state.navigation.state === "loading") {
    const {pathname, search, hash} = router!.state.navigation.location;
    url.pathname = pathname;
    url.search = search;
    url.hash = hash;
  }
  // Preserve the location the user was trying to get to
  const safeRedirectPath = url.searchParams.get("redirect") ?? url.pathname + url.search + url.hash;
  const redirectUrlParams = new URLSearchParams();

  // Don't allow redirecting to login or logout pages as this would be very confusing
  // and could result in a cycle
  if (canRedirectTo(safeRedirectPath)) {
    redirectUrlParams.set("redirect", safeRedirectPath);
  }
  return redirectUrlParams;
}

export function shouldShowRegistrationLink() {
  return !!getRedirectParams().get("redirect")?.startsWith("/consent");
}

function getAuthenticationAction(): string {
  return new URL(window.location.href).searchParams.get("auth_action") ?? "";
}

function getEmailHint(): string | null {
  return new URL(window.location.href).searchParams.get("email_hint");
}

function redirectPreservingUrl(
  target: RedirectTarget,
  location: string,
  searchParams: URLSearchParams = new URLSearchParams(),
) {
  const fullSearchParams = new URLSearchParams([...searchParams, ...getRedirectParams()]);
  return new RedirectResponse(`${location}?${fullSearchParams}`, target);
}

export function redirectRestoringUrl() {
  const redirectUrl = getRedirectParams().get("redirect");
  if (redirectUrl && isSafeInternalPath(redirectUrl)) {
    return new RedirectResponse(redirectUrl, RedirectTarget.Unknown);
  } else {
    return new RedirectResponse("/", RedirectTarget.Unknown);
  }
}

export function redirectUnauthorized() {
  if (getAuthenticationAction() === "register") {
    return redirectToRegister();
  } else {
    return redirectToLogin();
  }
}

export function buildLinkToConsent(
  authAction: "login" | "register" = "login",
  redirectUrl?: string,
  accountId?: AccountId,
  emailHint?: string,
): string {
  const {app_url} = getFrontendEnvironment();
  const params = new URLSearchParams({
    external_redirect: redirectUrl ?? window.location.href,
    auth_action: authAction,
    ...(accountId ? {account_id: accountId} : undefined),
    ...(emailHint ? {email_hint: emailHint} : undefined),
  });
  return `${app_url}/consent?${params}`;
}

export function redirectToConsent(
  authAction: "login" | "register" = "login",
  redirectUrl?: string,
  account_id?: AccountId,
) {
  window.location.assign(buildLinkToConsent(authAction, redirectUrl, account_id));
}

function redirectToLogin() {
  const emailHint = getEmailHint();

  const params = new URLSearchParams({...(emailHint && {email_hint: emailHint})});

  return redirectPreservingUrl(RedirectTarget.Login, "/login", params);
}

function redirectToRegister() {
  return redirectPreservingUrl(RedirectTarget.Register, "/register");
}

export function redirectToAccountSelector() {
  clearSessionState();
  return redirectPreservingUrl(RedirectTarget.AccountSelector, "/account-selector");
}

export function redirectToPortalSelector() {
  clearSessionState();
  return redirectPreservingUrl(RedirectTarget.PortalSelector, "/portal-selector");
}

export function redirectToProductSelector() {
  clearSessionState();
  return redirectPreservingUrl(RedirectTarget.ProductSelector, "/product-selector");
}

const accountIdSchema = Type.Union([Type.Null(), Type.AccountId()]);

export function getAccountId() {
  // Allow "account_id" to be specified in URL
  const url = new URL(window.location.href);
  const accountId = Value.Decode(accountIdSchema, url.searchParams.get("account_id"));
  if (accountId) {
    // Account ID was specified, put it in session storage and then remove it
    // from the URL
    writeSessionState("account_id", accountIdSchema, accountId);
    url.searchParams.delete("account_id");
    window.history.replaceState({}, document.title, url);
  }
  return readSessionState("account_id", accountIdSchema, null);
}

export function subscribeAccountId(cb: () => void) {
  return subscribeSessionState("account_id", cb);
}
